// appointment.reducer.ts
import { createSlice } from '@reduxjs/toolkit'
import {
  fetchAllAppointments,
  addAppointment,
  updateAppointment,
  deleteAppointment,
  updateAppointmentStatus,
} from '../actions/appointment.action'
import { AppointmentResponse } from '@dataTypes/appointment.type'

interface AppointmentState {
  data: AppointmentResponse[]
  loading: {
    fetch: boolean
    add: boolean
    update: boolean
    delete: boolean
  }
  error: {
    fetch: string | null
    add: string | null
    update: string | null
    delete: string | null
  }
}

const initialState: AppointmentState = {
  data: [],
  loading: {
    fetch: false,
    add: false,
    update: false,
    delete: false,
  },
  error: {
    fetch: null,
    add: null,
    update: null,
    delete: null,
  },
}

const appointmentSlice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllAppointments.pending, (state) => {
        state.loading.fetch = true
        state.error.fetch = null
      })
      .addCase(fetchAllAppointments.fulfilled, (state, action) => {
        state.data = action.payload
        state.loading.fetch = false
      })
      .addCase(fetchAllAppointments.rejected, (state, action) => {
        state.loading.fetch = false
        state.error.fetch = action.payload as string
      })

      // Add
      .addCase(addAppointment.pending, (state) => {
        state.loading.add = true
        state.error.add = null
      })
      .addCase(addAppointment.fulfilled, (state) => {
        state.loading.add = false
      })
      .addCase(addAppointment.rejected, (state, action) => {
        state.loading.add = false
        state.error.add = action.payload as string
      })

      // Update
      .addCase(updateAppointment.pending, (state) => {
        state.loading.update = true
        state.error.update = null
      })
      .addCase(updateAppointment.fulfilled, (state, action) => {
        const index = state.data.findIndex(
          (a) => a.appointment_id === action.payload.appointment_id,
        )
        if (index !== -1) {
          state.data[index] = { ...state.data[index], ...action.payload }
        }
        state.loading.update = false
      })
      .addCase(updateAppointment.rejected, (state, action) => {
        state.loading.update = false
        state.error.update = action.payload as string
      })

      // Delete
      .addCase(deleteAppointment.pending, (state) => {
        state.loading.delete = true
        state.error.delete = null
      })
      .addCase(deleteAppointment.fulfilled, (state, action) => {
        state.loading.delete = false
        state.data = state.data.filter(
          (a) => a.appointment_id !== action.payload,
        )
      })
      .addCase(deleteAppointment.rejected, (state, action) => {
        state.loading.delete = false
        state.error.delete = action.payload as string
      })

      // Update Status
      .addCase(updateAppointmentStatus.pending, (state) => {
        state.loading.update = true
        state.error.update = null
      })
      .addCase(updateAppointmentStatus.fulfilled, (state, action) => {
        if (!state.data) return
        const index = state.data.findIndex(
          (appointment) =>
            appointment.appointment_id === action.payload.appointment_id,
        )

        if (index !== -1) {
          state.data[index] = {
            ...state.data[index],
            ...action.payload,
          }
        }

        state.loading.update = false
      })
      .addCase(updateAppointmentStatus.rejected, (state, action) => {
        state.loading.update = false
        state.error.update = action.payload as string
      })
  },
})

export default appointmentSlice.reducer
